import React, { useContext, useEffect, useState } from "react"
import i18n from "i18n"
import OptionSwitch from "./OptionSwitch"
import { DOWNLOAD_TYPES, INPUTS } from "hooks/budgeting/useDownloadBudgetConfig"
import PropTypes from "prop-types"
import useEntitlementGuard from "hooks/useEntitlementGuard"
import {
  EXPORT_EXCEL_CLIENT_PRICE,
  EXPORT_EXCEL_COST_PRICE,
  EXPORT_EXCEL_DECOMPOSITION,
  EXPORT_EXCEL_MEASUREMENTS,
  EXPORT_EXCEL_COMMENTS,
  EXPORT_EXCEL_DECOMPOSITION_FILTER,
  EXPORT_LISTO_XLS,
  GRANTED,
} from "constants/entitlements"
import SubscriptionContext from "context/subscription/subscriptionContext"
import DownloadBudgetEnum from "common/DownloadBudgetEnum"

import BudgetExportSelect from "./BudgetExportSelect"

const ExcelOptions = ({
  setDownloadBudgetConfig,
  clientPrice,
  setClientPrice,
  costPrice,
  setCostPrice,
  decomposition,
  setDecomposition,
  measure,
  setMeasure,
  comments,
  setComments,
  decompositionFilter,
  setDecompositionFilter,
  decompositionFilterRef,
  fromTab,
  certificationAtOrigin: certificationAtOriginProp,
  setCertificationAtOrigin: setCertificationAtOriginProp,
}) => {
  const tipo = DOWNLOAD_TYPES.EXCEL
  const [internalCertAtOrigin, setInternalCertAtOrigin] = useState(false)
  const useLifted =
    typeof setCertificationAtOriginProp === "function" &&
    typeof certificationAtOriginProp === "boolean"
  const certificationAtOrigin = useLifted
    ? certificationAtOriginProp
    : internalCertAtOrigin
  const setCertificationAtOrigin = useLifted
    ? setCertificationAtOriginProp
    : setInternalCertAtOrigin
  const isCertTab =
    fromTab === DownloadBudgetEnum.FROM_CERTIFICATION ||
    fromTab === DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION
  const { subscriptionState } = useContext(SubscriptionContext)

  const [checkEntitlement, entitlementDialog] = useEntitlementGuard()

  const entitlementDialogTitle = (configKey, titleOverride) => {
    if (titleOverride) return titleOverride
    const i18nKeyByConfigKey = {
      [INPUTS.MEASURES]: "measurements",
      [INPUTS.COMMENTS]: "comments",
    }
    const i18nKey = i18nKeyByConfigKey[configKey] ?? configKey
    return i18n.t(`downloadBudget.${i18nKey}`)
  }

  const handleEntitlementCheck = (
    permission,
    state,
    setState,
    configKey,
    titleOverride,
  ) => {
    const hasPermission =
      permission === GRANTED ||
      subscriptionState?.subscription_entitlements?.some(
        ent => ent.feature_name === permission && ent.is_enabled === true,
      )

    const handleBlocked = () => {
      checkEntitlement(
        permission,
        entitlementDialogTitle(configKey, titleOverride),
      )
    }

    useEffect(() => {
      if (!hasPermission && state) {
        setState(false)
        setDownloadBudgetConfig(tipo, configKey, false)
      }
    }, [
      hasPermission,
      state,
      setState,
      setDownloadBudgetConfig,
      tipo,
      configKey,
    ])

    return hasPermission ? null : handleBlocked
  }

  useEffect(() => {
    if (fromTab !== DownloadBudgetEnum.DEFAULT) {
      if (!clientPrice) {
        setClientPrice(true)
        setDownloadBudgetConfig(tipo, INPUTS.CLIENT_PRICE, true)
      }
      if (costPrice) {
        setCostPrice(false)
        setDownloadBudgetConfig(tipo, INPUTS.COST_PRICE, false)
      }
    }
  }, [fromTab])

  const clientPriceSwitch = {
    i18nKey: "downloadBudget.clientPrice",
    state: clientPrice,
    setState: setClientPrice,
    key: `excel_${INPUTS.CLIENT_PRICE}`,
    inputKey: INPUTS.CLIENT_PRICE,
    tipo: tipo,
    setDownloadBudgetConfig: setDownloadBudgetConfig,
    onToggleBlocked: handleEntitlementCheck(
      EXPORT_EXCEL_CLIENT_PRICE,
      clientPrice,
      setClientPrice,
      INPUTS.CLIENT_PRICE,
    ),
  }

  const costPriceSwitch = {
    i18nKey: "downloadBudget.costPrice",
    state: costPrice,
    setState: setCostPrice,
    key: `excel_${INPUTS.COST_PRICE}`,
    inputKey: INPUTS.COST_PRICE,
    tipo: tipo,
    setDownloadBudgetConfig: setDownloadBudgetConfig,
    onToggleBlocked: handleEntitlementCheck(
      EXPORT_EXCEL_COST_PRICE,
      costPrice,
      setCostPrice,
      INPUTS.COST_PRICE,
    ),
  }

  const excelData = [
    {
      i18nKey: "downloadBudget.decomposition",
      state: decomposition,
      setState: setDecomposition,
      key: `excel_${INPUTS.PART_DECOMPOSITION}`,
      inputKey: INPUTS.PART_DECOMPOSITION,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleEntitlementCheck(
        EXPORT_EXCEL_DECOMPOSITION,
        decomposition,
        setDecomposition,
        INPUTS.PART_DECOMPOSITION,
      ),
    },
    {
      i18nKey: "downloadBudget.measurements",
      state: measure,
      setState: setMeasure,
      key: `excel_${INPUTS.MEASURES}`,
      inputKey: INPUTS.MEASURES,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleEntitlementCheck(
        EXPORT_EXCEL_MEASUREMENTS,
        measure,
        setMeasure,
        INPUTS.MEASURES,
      ),
    },
    {
      i18nKey: "downloadBudget.comments",
      state: comments,
      setState: setComments,
      key: `excel_${INPUTS.COMMENTS}`,
      inputKey: INPUTS.COMMENTS,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleEntitlementCheck(
        EXPORT_EXCEL_COMMENTS,
        comments,
        setComments,
        INPUTS.COMMENTS,
      ),
    },
  ]

  const decompositionFilterOptions = [
    { value: 0, label: i18n.t("downloadBudget.decompositionFilter.all") },
    { value: 1, label: i18n.t("downloadBudget.decompositionFilter.labor") },
    { value: 2, label: i18n.t("downloadBudget.decompositionFilter.machinery") },
    { value: 3, label: i18n.t("downloadBudget.decompositionFilter.materials") },
    {
      value: 4,
      label: i18n.t("downloadBudget.decompositionFilter.additionalWaste"),
    },
    {
      value: 5,
      label: i18n.t("downloadBudget.decompositionFilter.wasteClassification"),
    },
  ]

  const handleDecompositionFilterChange = event => {
    const next = Number(event.target.value)
    const hasPermission = subscriptionState?.subscription_entitlements?.some(
      ent =>
        ent.feature_name === EXPORT_EXCEL_DECOMPOSITION_FILTER &&
        ent.is_enabled === true,
    )

    if (!hasPermission) {
      checkEntitlement(
        EXPORT_EXCEL_DECOMPOSITION_FILTER,
        i18n.t("downloadBudget.decompositionFilter.all"),
      )
      setDecompositionFilter(0)
      setDownloadBudgetConfig(tipo, INPUTS.DECOMPOSITION_FILTER, 0)
    } else {
      setDecompositionFilter(next)
      setDownloadBudgetConfig(tipo, INPUTS.DECOMPOSITION_FILTER, next)
    }
  }

  const certificationAtOriginSwitch = {
    i18nKey: "downloadBudget.certificationAtOrigin",
    state: certificationAtOrigin,
    setState: setCertificationAtOrigin,
    key: `excel_${INPUTS.CERTIFICATION_AT_ORIGIN}`,
    inputKey: INPUTS.CERTIFICATION_AT_ORIGIN,
    tipo: tipo,
    setDownloadBudgetConfig: setDownloadBudgetConfig,
    onToggleBlocked: handleEntitlementCheck(
      EXPORT_LISTO_XLS,
      certificationAtOrigin,
      setCertificationAtOrigin,
      INPUTS.CERTIFICATION_AT_ORIGIN,
      i18n.t("certHistory.downloadCertificationOriginTooltip"),
    ),
  }

  return (
    <>
      {isCertTab && (
        <OptionSwitch
          key={certificationAtOriginSwitch.key}
          {...certificationAtOriginSwitch}
        />
      )}
      {!(isCertTab && certificationAtOrigin) && (
        <>
          {fromTab === DownloadBudgetEnum.DEFAULT && (
            <>
              <BudgetExportSelect
                inputRef={decompositionFilterRef}
                value={
                  decompositionFilter !== undefined &&
                  decompositionFilter !== null
                    ? decompositionFilter
                    : 0
                }
                options={decompositionFilterOptions}
                onChange={handleDecompositionFilterChange}
              />
              <OptionSwitch
                key={clientPriceSwitch.key}
                {...clientPriceSwitch}
              />
              <OptionSwitch key={costPriceSwitch.key} {...costPriceSwitch} />
            </>
          )}
          {excelData.map(opt => (
            <OptionSwitch key={opt.key} {...opt} />
          ))}
        </>
      )}
      {entitlementDialog}
    </>
  )
}

ExcelOptions.propTypes = {
  clientPrice: PropTypes.bool.isRequired,
  comments: PropTypes.bool.isRequired,
  costPrice: PropTypes.bool.isRequired,
  decomposition: PropTypes.bool.isRequired,
  measure: PropTypes.bool.isRequired,
  decompositionFilter: PropTypes.number,
  decompositionFilterRef: PropTypes.any,
  setClientPrice: PropTypes.func.isRequired,
  setComments: PropTypes.func.isRequired,
  setCostPrice: PropTypes.func.isRequired,
  setDecomposition: PropTypes.func.isRequired,
  setDownloadBudgetConfig: PropTypes.func.isRequired,
  setMeasure: PropTypes.func.isRequired,
  setDecompositionFilter: PropTypes.func,
  fromTab: PropTypes.oneOf(Object.values(DownloadBudgetEnum)),
  certificationAtOrigin: PropTypes.bool,
  setCertificationAtOrigin: PropTypes.func,
}

export default ExcelOptions
